import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { HeartPulse, ShieldCheck, Check, X, Loader2, FileText, Pause, Play, ExternalLink, Wallet, DollarSign, RefreshCw, TrendingUp } from "lucide-react";

const DOC_LABELS = { resume: "Resume", license_doc: "License", gov_id: "Gov ID", profile_photo: "Photo" };

export default function AdminNurseApplications() {
  const [nurses, setNurses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});

  const load = () => {
    setLoading(true);
    api.get("/admin/nurses").then(({ data }) => setNurses(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.post(`/admin/nurses/${id}/status`, { status, notes: notes[id] || null }); toast.success(`Nurse ${status}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
  };
  const docUrl = (n, key) => `${process.env.REACT_APP_BACKEND_URL}/api/nurses/${n.nurse_id}/documents/${key}`;

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>;

  const STATUS = { pending: "bg-amber-50 text-amber-600", active: "bg-green-50 text-green-600", suspended: "bg-slate-100 text-slate-500", rejected: "bg-red-50 text-red-600" };
  const pending = nurses.filter((n) => n.status === "pending");
  const others = nurses.filter((n) => n.status !== "pending");

  const Row = ({ n }) => (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`nurse-${n.nurse_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-10 w-10 rounded-xl bg-cobalt-light flex items-center justify-center shrink-0"><HeartPulse className="w-5 h-5 text-cobalt" /></div>
          <div className="min-w-0">
            <div className="font-semibold text-navy">{n.name || `${n.first_name || ""} ${n.last_name || ""}`}</div>
            <div className="text-xs text-slate-500 truncate">{n.email}{n.phone ? ` · ${n.phone}` : ""}</div>
            <div className="text-xs text-slate-400 mt-0.5 flex flex-wrap gap-x-3">
              <span>Lic #{n.license_number || "—"} ({(n.states_licensed || []).join(", ") || "—"})</span>
              <span>{n.years_experience || "—"} yrs</span>
              {n.npi && <span>NPI {n.npi}</span>}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">{n.nursing_school || "—"} · {n.degrees || "—"} · {(n.certifications || []).join(", ") || "no certs"}</div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {n.trauma_experience && <Tag>Trauma</Tag>}{n.ed_experience && <Tag>ED</Tag>}{n.case_management_experience && <Tag>Case Mgmt</Tag>}
              {n.banking_on_file && <Tag>Banking ••{n.banking_last4}</Tag>}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.keys(n.documents_uploaded || {}).map((k) => (
                <a key={k} href={docUrl(n, k)} target="_blank" rel="noreferrer" data-testid={`nurse-doc-link-${n.nurse_id}-${k}`}
                  className="inline-flex items-center gap-1 text-xs font-medium text-cobalt bg-cobalt-light px-2.5 py-1 rounded-full hover:bg-cobalt hover:text-white">
                  <FileText className="w-3 h-3" /> {DOC_LABELS[k] || k} <ExternalLink className="w-3 h-3" />
                </a>
              ))}
            </div>
          </div>
        </div>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize shrink-0 ${STATUS[n.status]}`}>{n.status}</span>
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {(n.status === "pending" || n.status === "rejected" || n.status === "suspended") && (
          <input value={notes[n.nurse_id] || ""} onChange={(e) => setNotes({ ...notes, [n.nurse_id]: e.target.value })}
            placeholder="Optional note to nurse" data-testid={`nurse-notes-${n.nurse_id}`}
            className="flex-1 min-w-[180px] bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm outline-none focus:border-cobalt" />
        )}
        {n.status === "pending" && <>
          <button onClick={() => setStatus(n.nurse_id, "active")} data-testid={`nurse-approve-${n.nurse_id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-cobalt text-white text-sm font-medium hover:bg-cobalt-hover"><Check className="w-4 h-4" /> Approve</button>
          <button onClick={() => setStatus(n.nurse_id, "rejected")} data-testid={`nurse-reject-${n.nurse_id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-600 text-sm hover:bg-red-50 hover:text-red-600"><X className="w-4 h-4" /> Reject</button>
        </>}
        {n.status === "active" && <button onClick={() => setStatus(n.nurse_id, "suspended")} data-testid={`nurse-suspend-${n.nurse_id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-sm hover:bg-red-50 hover:text-red-600"><Pause className="w-4 h-4" /> Suspend</button>}
        {(n.status === "suspended" || n.status === "rejected") && <button onClick={() => setStatus(n.nurse_id, "active")} data-testid={`nurse-reinstate-${n.nurse_id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-cobalt text-white text-sm font-medium hover:bg-cobalt-hover"><Play className="w-4 h-4" /> Reinstate</button>}
      </div>
      {n.status === "active" && <NurseAdminExtras n={n} />}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="admin-nurses">
      <PayoutsPanel />
      {pending.length > 0 && (
        <div>
          <h3 className="font-heading font-semibold text-navy mb-2 flex items-center gap-2">Pending RN Applications <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{pending.length}</span></h3>
          <div className="space-y-3">{pending.map((n) => <Row key={n.nurse_id} n={n} />)}</div>
        </div>
      )}
      <div>
        {pending.length > 0 && <h3 className="font-heading font-semibold text-navy mb-2 mt-4">All Nurses</h3>}
        {others.length === 0 && pending.length === 0
          ? <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center text-slate-500">No RN applications yet.</div>
          : <div className="space-y-3">{others.map((n) => <Row key={n.nurse_id} n={n} />)}</div>}
      </div>
    </div>
  );
}

const Tag = ({ children }) => <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full"><ShieldCheck className="w-3 h-3" /> {children}</span>;

function NurseAdminExtras({ n }) {
  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState(n.payout_rate ?? "");
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const toggle = async () => {
    const next = !open; setOpen(next);
    if (next && !data) {
      try { const { data } = await api.get(`/admin/nurses/${n.nurse_id}/metrics`); setData(data); } catch {}
    }
  };
  const saveRate = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/nurses/${n.nurse_id}/payout-rate`, { payout_rate: rate === "" ? null : Number(rate) });
      toast.success("Payout rate saved");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  const m = data?.metrics || {};
  const e = data?.earnings || {};
  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-slate-400">Payout rate</span>
          <span className="text-slate-400">$</span>
          <input value={rate} onChange={(ev) => setRate(ev.target.value)} placeholder="default" data-testid={`nurse-rate-${n.nurse_id}`}
            className="w-20 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-sm outline-none focus:border-cobalt" />
          <button onClick={saveRate} disabled={saving} data-testid={`nurse-rate-save-${n.nurse_id}`} className="text-xs font-semibold px-2.5 py-1 rounded-full bg-navy text-white">Save</button>
        </div>
        <button onClick={toggle} data-testid={`nurse-metrics-toggle-${n.nurse_id}`} className="text-xs font-semibold px-3 py-1 rounded-full border border-slate-200 text-cobalt hover:bg-cobalt-light flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> {open ? "Hide" : "Metrics"}</button>
      </div>
      {open && (
        <div className="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-2 text-center" data-testid={`nurse-metrics-${n.nurse_id}`}>
          {[["Completed", m.completed ?? 0], ["Completion", `${m.completion_pct ?? 0}%`], ["Calls", m.total_calls ?? 0],
            ["Quality", m.quality_score ?? "—"], ["Lifetime", `$${Number(e.lifetime_earnings || 0).toFixed(0)}`], ["Pending", `$${Number(e.pending_payouts || 0).toFixed(0)}`]].map(([l, v]) => (
            <div key={l} className="rounded-lg bg-slate-50 p-2"><div className="text-[10px] text-slate-400 uppercase">{l}</div><div className="font-bold text-navy text-sm">{v}</div></div>
          ))}
        </div>
      )}
    </div>
  );
}

function PayoutsPanel() {
  const [d, setD] = useState(null);
  const [rate, setRate] = useState("");
  const [running, setRunning] = useState(false);
  const load = () => api.get("/admin/rn-payouts").then(({ data }) => { setD(data); setRate(data.config?.rate_per_intake ?? ""); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const saveRate = async () => {
    try { await api.put("/admin/rn-payouts/config", { rate_per_intake: Number(rate) }); toast.success("Default rate saved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
  };
  const run = async () => {
    setRunning(true);
    try { const { data } = await api.post("/admin/rn-payouts/run"); toast.success(`Ran ${data.payouts_created} payout(s) · $${data.total_paid.toFixed(2)}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed"); }
    finally { setRunning(false); }
  };
  if (!d) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="rn-payouts-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><Wallet className="w-5 h-5 text-cobalt" /> RN Weekly Payouts <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">SANDBOX</span></h3>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 text-sm"><span className="text-slate-400">Default $</span>
            <input value={rate} onChange={(e) => setRate(e.target.value)} data-testid="rn-payout-rate" className="w-20 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-cobalt" />
            <button onClick={saveRate} data-testid="rn-payout-rate-save" className="text-xs font-semibold px-3 py-1.5 rounded-full bg-navy text-white">Save</button>
          </div>
          <button onClick={run} disabled={running || d.total_pending <= 0} data-testid="rn-run-payouts" className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-cobalt text-white text-sm font-semibold hover:bg-cobalt-hover disabled:opacity-50">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Run weekly payouts
          </button>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-4 text-sm">
        <div><span className="text-slate-400">Total pending </span><span className="font-heading font-bold text-amber-600">${Number(d.total_pending).toLocaleString()}</span></div>
        <div><span className="text-slate-400">Nurses awaiting </span><span className="font-semibold text-navy">{d.pending_by_nurse?.length || 0}</span></div>
      </div>
      {d.pending_by_nurse?.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="rn-pending-list">
          {d.pending_by_nurse.map((p) => (
            <div key={p.nurse_id} className="flex items-center justify-between text-sm bg-slate-50 rounded-lg px-3 py-2">
              <span className="text-navy font-medium">{p.name} <span className="text-xs text-slate-400">· {p.pending_count} intake(s)</span>{!p.banking_on_file && <span className="ml-2 text-[11px] text-red-500">no banking</span>}</span>
              <span className="font-semibold text-navy">${Number(p.pending_amount).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
      {d.history?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="text-xs text-slate-400 mb-1">Recent payout runs</div>
          {d.history.slice(0, 5).map((h) => (
            <div key={h.payout_id} className="flex items-center justify-between text-xs text-slate-500 py-1">
              <span>{h.name} · {h.count} intake(s) · {h.period}</span><span className="font-semibold text-emerald-600">${Number(h.amount).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
